import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Save, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/AdminLayout";
import { PaymentTemplate } from "@/components/PaymentTemplate";
import { PreviewDialog } from "@/components/PreviewDialog";
import { api, apiError } from "@/lib/api";
import { DEMO_PREVIEW } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { TestEmailButton } from "@/components/TestEmailButton";
import { PropertiesEditor } from "@/components/PropertiesEditor";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Textarea } from "@/components/ui/textarea";
import { WA_DEFAULT } from "@/lib/format";

const WhatsAppFields = ({ draft, change, readOnly }) => (
  <div className="space-y-3" data-testid="hotel-whatsapp-fields">
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Mensaje de WhatsApp</Label>
    {[["es", "Español"], ["en", "English"]].map(([l, name]) => (
      <div key={l} className="space-y-1">
        <p className="text-xs text-slate-500">{name}</p>
        <Textarea rows={3} disabled={readOnly} value={draft[`whatsapp_${l}`] || ""} placeholder={WA_DEFAULT[l]}
          onChange={(e) => change(`whatsapp_${l}`, e.target.value)} data-testid={`hotel-whatsapp-${l}`} />
      </div>
    ))}
    <p className="text-xs text-slate-500">Usa {"{nombre}"}, {"{hotel}"} y {"{enlace}"} (obligatorio). Vacío = mensaje predeterminado.</p>
  </div>
);
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLORS = [["primary_color", "Primario"], ["secondary_color", "Secundario"], ["accent_color", "Acento"], ["background_color", "Fondo"]];

const ColorField = ({ k, label, value, onChange }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={(e) => onChange(k, e.target.value.toUpperCase())} className="h-10 w-12 cursor-pointer rounded-md border border-slate-200 bg-white p-1" data-testid={`color-picker-${k}`} />
      <Input value={value} onChange={(e) => onChange(k, e.target.value)} className="font-mono" data-testid={`color-input-${k}`} />
    </div>
  </div>
);

export default function Hotels() {
  const [hotels, setHotels] = useState([]);
  const [slug, setSlug] = useState("hard-rock");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [full, setFull] = useState(false);
  const [propsDirty, setPropsDirty] = useState(false);
  const [pendingSlug, setPendingSlug] = useState(null);
  const pickTab = (s) => { if (s === slug) return; if (propsDirty) setPendingSlug(s); else setSlug(s); };
  const { user } = useAuth();
  const readOnly = user?.role !== "admin";

  useEffect(() => { api.get("/hotels").then((r) => setHotels(r.data)); }, []);
  useEffect(() => {
    const h = hotels.find((x) => x.slug === slug);
    if (h) { setDraft({ ...h }); }
  }, [slug, hotels]);

  const change = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 300 * 1024) { toast.error("El logotipo debe pesar máximo 300 KB"); return; }
    const reader = new FileReader();
    reader.onload = () => change("logo_data", reader.result);
    reader.readAsDataURL(file);
  };
  const save = async () => {
    setSaving(true);
    try {
      const { slug: s, display_name, primary_color, secondary_color, accent_color, background_color, logo_data, language, whatsapp_es, whatsapp_en } = draft;
      const { data } = await api.put(`/hotels/${s}`, { display_name, primary_color, secondary_color, accent_color, background_color, logo_data, language: language || "es",
        whatsapp_es: whatsapp_es?.trim() || null, whatsapp_en: whatsapp_en?.trim() || null });
      setHotels((hs) => hs.map((h) => (h.slug === s ? data : h)));
      toast.success("Plantilla guardada");
    } catch (e) { toast.error(apiError(e).message); } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="Plantillas" subtitle={readOnly ? "Consulta cómo se ve cada plantilla. Solo el administrador puede editarlas." : "Edita el nombre visible, colores, logotipo e idioma de cada plantilla. Los cambios se reflejan en sus páginas de pago."} />
      <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
        {hotels.map((h) => (
          <button key={h.slug} onClick={() => pickTab(h.slug)} data-testid={`hotel-tab-${h.slug}`}
            className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${slug === h.slug ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: h.primary_color }} />{h.name}
          </button>
        ))}
      </div>
      {draft && (
        <div className="grid gap-6 xl:grid-cols-[380px_1fr]">
          <fieldset disabled={readOnly} className="h-fit space-y-5 rounded-2xl border border-slate-200 bg-white p-6 disabled:opacity-70" data-testid="hotel-editor">
            <WhatsAppFields draft={draft} change={change} readOnly={readOnly} />
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Idioma de la página de pago</Label>
              <Select disabled={readOnly} value={draft.language || "es"} onValueChange={(v) => change("language", v)}>
                <SelectTrigger data-testid="hotel-language-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="es" data-testid="hotel-language-es">Español</SelectItem><SelectItem value="en" data-testid="hotel-language-en">English</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nombre visible</Label>
              <Input value={draft.display_name} onChange={(e) => change("display_name", e.target.value)} data-testid="hotel-display-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">{COLORS.map(([k, l]) => <ColorField key={k} k={k} label={l} value={draft[k]} onChange={change} />)}</div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Logotipo</Label>
              <div className="flex h-20 items-center justify-center rounded-xl border border-dashed border-slate-300" style={{ background: draft.background_color }}>
                {draft.logo_data ? <img src={draft.logo_data} alt="Logotipo" className="max-h-14 max-w-[200px] object-contain" data-testid="hotel-logo-preview" />
                  : <span className="text-xs text-slate-400">Sin logotipo · se muestra el nombre en texto</span>}
              </div>
              <div className="flex gap-2">
                {!readOnly && <label className="inline-flex cursor-pointer items-center rounded-md border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50">
                  <Upload size={15} className="mr-2" />Subir imagen<input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="hotel-logo-input" />
                </label>}
                {!readOnly && draft.logo_data && <Button variant="ghost" size="sm" onClick={() => change("logo_data", null)} data-testid="hotel-logo-remove"><X size={15} className="mr-1" />Quitar</Button>}
              </div>
              <p className="text-xs text-slate-400">PNG o SVG, máximo 300 KB. Usa los logotipos oficiales cuando los tengas.</p>
            </div>
            {!readOnly && <Button onClick={save} disabled={saving} className="w-full bg-[#2F5BFF] hover:bg-[#1F45E0]" data-testid="hotel-save-btn"><Save size={16} className="mr-2" />Guardar cambios</Button>}
            {!readOnly && <TestEmailButton hotel={draft} />}
          </fieldset>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5 text-xs text-slate-500">
              <span>Vista previa en vivo · datos de demostración</span>
              <Button variant="ghost" size="sm" onClick={() => setFull(true)} data-testid="hotel-preview-full-btn"><Maximize2 size={14} className="mr-1" />Pantalla completa</Button>
            </div>
            <div className="h-[640px] overflow-y-auto"><PaymentTemplate hotel={draft} data={DEMO_PREVIEW} preview /></div>
          </div>
        </div>
      )}
      {draft && !readOnly && <PropertiesEditor hotel={hotels.find((h) => h.slug === slug) || draft} onSaved={(data) => setHotels((hs) => hs.map((h) => (h.slug === data.slug ? data : h)))} onDirtyChange={setPropsDirty} />}
      <ConfirmDialog open={!!pendingSlug} onOpenChange={(o) => { if (!o) setPendingSlug(null); }}
        title="Tienes cambios sin guardar" description="Los hoteles y habitaciones que modificaste en esta plantilla no se han guardado. Si cambias de plantilla, se perderán."
        confirmLabel="Descartar y cambiar" onConfirm={() => { setPropsDirty(false); setSlug(pendingSlug); setPendingSlug(null); }} />
      <PreviewDialog open={full} onOpenChange={setFull} hotel={draft} data={DEMO_PREVIEW} />
    </>
  );
}
